import { useTranslation } from 'react-i18next'
import { useFormikContext } from 'formik'
import { Avatar, Button, IconButton, TextField, Typography } from '@mui/material'

import { SdgIconInput } from './sdg-icon-input.component'

import { useStyles } from './styles'
import { ETranslationNs, IUnSdgForm } from 'src/types'
import { useRef } from 'react'
import { DeleteIcon } from 'src/assets/icons'
import { StubImageButton } from 'src/components/media-input/stub-image-button.component'
import { HelperWrapper } from 'src/components/helper-wrapper'

interface IUnSdgFormProps {
  isEditing?: boolean
}

const UnSdgForm = ({ isEditing }: IUnSdgFormProps) => {
  const [t] = useTranslation([ETranslationNs.Common, ETranslationNs.Form])
  const { errors, values, handleChange, isSubmitting, dirty } = useFormikContext<IUnSdgForm>()
  const inputRef = useRef<HTMLInputElement>(null)
  const classes = useStyles()

  const handleIconClick = () => {
    inputRef.current?.click()
  }

  const handleImageRemove = () => {
    handleChange({
      target: {
        name: 'image',
        value: { icon: '', file: null },
      },
    })
    inputRef.current?.value && (inputRef.current.value = '')
  }

  const hasErrors = Object.keys(errors).length > 0

  return (
    <div className={classes.formWrapper}>
      <div className={classes.inputsWrapper}>
        <Typography className={classes.title}>
          {isEditing ? (
            <HelperWrapper t="un-sdg-form-title-edit" />
          ) : (
            <HelperWrapper t="un-sdg-form-title-create" />
          )}
        </Typography>
        <div>
          {values.image.icon ? (
            <div className={classes.imageWrapper}>
              <Avatar alt={values.name} src={values.image.icon} className={classes.image} />
              <IconButton
                aria-label="delete"
                className={classes.removeImageButton}
                onClick={handleImageRemove}
              >
                <DeleteIcon className={classes.removeImage} />
              </IconButton>
            </div>
          ) : (
            <div>
              <StubImageButton handleClick={handleIconClick} />
            </div>
          )}
          <Typography className={classes.warningText}>
            {errors.image?.icon && t(errors.image.icon)}&nbsp;
          </Typography>
        </div>
        <div className={classes.inputsWrapper}>
          <TextField
            autoComplete="off"
            variant="standard"
            id="name"
            name="name"
            onChange={handleChange}
            type="text"
            label={t('name-input-label.v', { ns: ETranslationNs.Form }) + '*'}
            value={values.name}
            helperText={
              <Typography className={classes.warningText}>
                {errors.name && t(errors.name)}&nbsp;
              </Typography>
            }
          />
          <TextField
            autoComplete="off"
            variant="standard"
            id="description"
            name="description"
            onChange={handleChange}
            type="text"
            label={t('description-input-label.v')}
            value={values.description}
            helperText={
              <Typography className={classes.warningText}>
                {errors.description && t(errors.description)}&nbsp;
              </Typography>
            }
          />
          <TextField
            autoComplete="off"
            variant="standard"
            id="more_info_url"
            name="more_info_url"
            onChange={handleChange}
            type="text"
            label={t('un-sdg-form-info-url-label.v')}
            value={values.more_info_url}
            helperText={
              <Typography className={classes.warningText}>
                {errors.more_info_url && t(errors.more_info_url)}&nbsp;
              </Typography>
            }
          />
        </div>
      </div>
      <Button variant="contained" disabled={isSubmitting || !dirty || hasErrors} type="submit">
        {isEditing ? (
          <HelperWrapper t="un-sdg-form-title-edit" />
        ) : (
          <HelperWrapper t="un-sdg-form-title-create" />
        )}
      </Button>
      <SdgIconInput inputRef={inputRef} />
    </div>
  )
}

export { UnSdgForm }
